import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import dayjs from 'dayjs';
import type { CalendarEvent } from './WeekTimelineView';
import EventCard from './EventCard';

interface WeekGridViewProps {
  events: CalendarEvent[];
  selectedDate: Date;
  weekStartDay?: number;
  onPressDay?: (date: Date) => void;
  onPressEvent?: (event: CalendarEvent) => void;
}

const PRIMARY = '#2e96e1';
const LANE_HEIGHT = 132;
const LANE_GAP = 5;

const WeekGridView: React.FC<WeekGridViewProps> = ({
  events,
  selectedDate,
  weekStartDay = 0,
  onPressDay,
  onPressEvent,
}) => {
  const today = dayjs().format('YYYY-MM-DD');

  const days = useMemo(() => {
    const current = dayjs(selectedDate);
    let daysBack = current.day() - weekStartDay;
    if (daysBack < 0) daysBack += 7;
    const start = current.subtract(daysBack, 'day');
    return Array.from({ length: 7 }, (_, i) => start.add(i, 'day'));
  }, [selectedDate, weekStartDay]);

  // Single-day events bucketed by day
  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const ev of events) {
      const s = dayjs(ev.start).startOf('day');
      const e = dayjs(ev.end).startOf('day');
      if (!s.isSame(e, 'day')) continue;
      const key = s.format('YYYY-MM-DD');
      const arr = map.get(key) || [];
      arr.push(ev);
      map.set(key, arr);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => a.start.getTime() - b.start.getTime());
    }
    return map;
  }, [events]);

  // Multi-day events split into per-week segments with lane assignments
  const weekData = useMemo(() => {
    type Segment = {
      event: CalendarEvent;
      startCol: number;
      endCol: number;
      continuesBefore: boolean;
      continuesAfter: boolean;
      lane: number;
    };
    const weekStart = days[0].startOf('day');
    const weekEnd = days[6].startOf('day');
    const raw: Omit<Segment, 'lane'>[] = [];
    for (const ev of events) {
      const evStart = dayjs(ev.start).startOf('day');
      const evEnd = dayjs(ev.end).startOf('day');
      if (evStart.isSame(evEnd, 'day')) continue;
      if (evEnd.isBefore(weekStart, 'day') || evStart.isAfter(weekEnd, 'day')) continue;
      const startCol = Math.max(0, evStart.diff(weekStart, 'day'));
      const endCol = Math.min(6, evEnd.diff(weekStart, 'day'));
      raw.push({
        event: ev,
        startCol,
        endCol,
        continuesBefore: evStart.isBefore(weekStart, 'day'),
        continuesAfter: evEnd.isAfter(weekEnd, 'day'),
      });
    }
    raw.sort(
      (a, b) =>
        a.startCol - b.startCol || (b.endCol - b.startCol) - (a.endCol - a.startCol),
    );
    const laneEnds: number[] = [];
    const segments: Segment[] = raw.map(seg => {
      let lane = laneEnds.findIndex(end => end < seg.startCol);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(seg.endCol);
      } else {
        laneEnds[lane] = seg.endCol;
      }
      return { ...seg, lane };
    });
    return { segments, laneCount: laneEnds.length };
  }, [days, events]);

  const weekHeaders = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) =>
        dayjs()
          .day((weekStartDay + i) % 7)
          .format('ddd')
          .toUpperCase(),
      ),
    [weekStartDay],
  );

  const lanesTopOffset = 36;
  const lanesHeight = weekData.laneCount * (LANE_HEIGHT + LANE_GAP);
  const cellTopPadding = lanesTopOffset + lanesHeight + 6;

  return (
    <View style={styles.container}>
      <View style={styles.weekHeaderRow}>
        {days.map((day, i) => {
          const isToday = day.format('YYYY-MM-DD') === today;
          return (
            <View key={i} style={styles.weekHeaderCell}>
              <Text style={[styles.weekHeaderText, isToday && styles.todayText]}>
                {weekHeaders[i]}
              </Text>
            </View>
          );
        })}
      </View>

      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={styles.weekRow}>
          {days.map((day, idx) => {
            const key = day.format('YYYY-MM-DD');
            const isToday = key === today;
            const dayEvents = eventsByDay.get(key) || [];
            return (
              <TouchableOpacity
                key={idx}
                style={[styles.cell, { paddingTop: cellTopPadding }]}
                onPress={() => onPressDay?.(day.hour(9).minute(0).second(0).toDate())}
                activeOpacity={0.7}
              >
                <View style={styles.dayNumberRowAbsolute}>
                  <View style={[styles.dayNumberWrap, isToday && styles.dayNumberWrapToday]}>
                    <Text style={[styles.dayNumber, isToday && styles.dayNumberToday]}>
                      {day.format('D')}
                    </Text>
                  </View>
                  <Text style={[styles.dayMonthLabel, isToday && styles.todayText]}>
                    {day.format('MMM')}
                  </Text>
                </View>
                <View style={styles.eventStack}>
                  {dayEvents.map(ev => (
                    <EventCard
                      key={ev.id}
                      event={ev}
                      density="compact"
                      onPress={() => onPressEvent?.(ev)}
                      style={styles.eventCardSpacing}
                    />
                  ))}
                </View>
              </TouchableOpacity>
            );
          })}

          {/* Multi-day spanning bars */}
          {weekData.segments.map((seg, sIdx) => {
            const widthPct = ((seg.endCol - seg.startCol + 1) / 7) * 100;
            const leftPct = (seg.startCol / 7) * 100;
            const top = lanesTopOffset + seg.lane * (LANE_HEIGHT + LANE_GAP);
            return (
              <View
                key={`${seg.event.id}-${sIdx}`}
                style={{
                  position: 'absolute',
                  top,
                  height: LANE_HEIGHT,
                  left: `${leftPct}%` as any,
                  width: `${widthPct}%` as any,
                  paddingLeft: seg.continuesBefore ? 0 : 3,
                  paddingRight: seg.continuesAfter ? 0 : 3,
                }}
              >
                <EventCard
                  event={seg.event}
                  density="compact"
                  continuesBefore={seg.continuesBefore}
                  continuesAfter={seg.continuesAfter}
                  onPress={() => onPressEvent?.(seg.event)}
                  style={styles.spanFill}
                />
              </View>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#fff' },
  weekHeaderRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  weekHeaderCell: { flex: 1, paddingVertical: 8, alignItems: 'center' },
  weekHeaderText: {
    fontSize: 14,
    color: '#444',
    letterSpacing: 0.5,
    fontWeight: '700',
  },
  todayText: { color: PRIMARY },
  scrollContent: { flexGrow: 1 },
  weekRow: { flex: 1, flexDirection: 'row', position: 'relative', minHeight: 600 },
  cell: {
    flex: 1,
    minHeight: 600,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: '#E0E0E0',
    padding: 6,
  },
  dayNumberRowAbsolute: {
    position: 'absolute',
    top: 5,
    left: 6,
    right: 6,
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 0,
  },
  dayNumberWrap: {
    minWidth: 30,
    height: 30,
    borderRadius: 15,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dayNumberWrapToday: { backgroundColor: PRIMARY },
  dayNumber: { fontSize: 16, color: '#111', fontWeight: '700' },
  dayNumberToday: { color: '#fff' },
  dayMonthLabel: { fontSize: 12, color: '#888', fontWeight: '600', marginLeft: 8 },
  eventStack: { marginTop: 6 },
  eventCardSpacing: { marginBottom: 5 },
  spanFill: { flex: 1 },
});

export default WeekGridView;
